import { NotificationMessage } from '@msrllabs/pt-api/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { accountTradeLabels } from '../../Constants';
import useDateFormat from '../../utils/useDateFormat';
import CustomAvatar from '../common/CustomAvatar';
import { notificationCategoryColor, NotificationCategoryIcon } from './NotificationCategoryMeta';

const Container = styled.div<{ $flat: boolean; $isUnread: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: ${props => (props.$flat ? '12px 24px' : '16px 22px')};
  border: ${props => (props.$flat ? 0 : `1px solid ${props.theme.brand.strokeSoft}`)};
  border-radius: ${props => (props.$flat ? 0 : '12px')};
  background: ${props => (props.$isUnread ? props.theme.brand.surface3 : props.$flat ? 'transparent' : props.theme.brand.gradCard)};
  cursor: pointer;

  &:hover {
    background: ${props => (props.$isUnread ? props.theme.brand.surface3 : props.theme.brand.surface2)};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.brand.royalCobalt};
    outline-offset: ${props => (props.$flat ? -2 : 2)}px;
  }

  @media (${props => props.theme.device.mobile}) {
    padding: ${props => (props.$flat ? '12px 20px' : '14px 16px')};
  }
`;

const IconCircle = styled.span<{ $color: string }>`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${props => props.theme.brand.surface2};
  color: ${props => props.$color};
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const AvatarWrap = styled.span`
  flex-shrink: 0;
  display: inline-flex;
`;

export const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TitleText = styled.span<{ $isUnread: boolean }>`
  font-size: 14px;
  font-weight: ${props => (props.$isUnread ? 800 : 700)};
  color: ${props => props.theme.brand.textPrimary};
`;

const UnreadDot = styled.button`
  width: 7px;
  height: 7px;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: ${props => props.theme.brand.accentInk};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${props => props.theme.brand.royalCobalt};
    outline-offset: 2px;
  }
`;

export const Preview = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${props => props.theme.brand.textSecondary};
  word-break: break-word;
`;

export const Meta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${props => props.theme.brand.textTertiary};
`;

/** Default leading glyph when a notification has no user avatars or category. */
export const NotificationFallbackGlyph = () => {
  const theme = useTheme();
  return (
    <IconCircle $color={theme.brand.textTertiary}>
      <NotificationCategoryIcon category={undefined} />
    </IconCircle>
  );
};

const Cta = styled.span`
  flex-shrink: 0;
  align-self: center;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: ${props => props.theme.brand.accentInk};
`;

type NotificationProps = {
  /** Flat full-width row (dropdown); default is a bordered card row (page). */
  flat?: boolean;
  handleGroupAvatar: (e: any) => void;
  notification: NotificationMessage;
  onClick: () => void;
  onToggleRead: (id: string, status: 'READ' | 'UNREAD') => void;
};

const Notification = ({ flat = false, handleGroupAvatar, notification, onClick, onToggleRead }: NotificationProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const timeAgo = useDateFormat();
  const { content, context, created, deeplink, id, items = [], isShowInline = false, status, title, type, category } = notification;

  const isUnread = status === 'UNREAD';
  const createdDate = new Date(created);
  const accentColor = notificationCategoryColor(category, theme);

  // handle context (accountType and cognitoId)
  const contextJson = context ? JSON.parse(context) : {};
  const { cognitoId, accountType = '' } = contextJson;
  const accountTradeLabel = accountTradeLabels[accountType] ? ` · ${t(accountTradeLabels[accountType])}` : '';

  const splitContent = content.split('\n');
  let firstLineContent = splitContent[0];
  if (splitContent.length > 1) firstLineContent += '...';

  // handle item if item contain cognitoId
  const cognitoIds = useMemo(() => {
    const itemCognitoIds = Array.from(
      new Set<string>(items?.filter(item => 'cognitoId' in item).map(item => item.cognitoId as string))
    );

    if (itemCognitoIds.length > 0) {
      return itemCognitoIds;
    }

    return cognitoId ? [cognitoId] : [];
  }, [cognitoId, items]);

  return (
    <Container
      $flat={flat}
      $isUnread={isUnread}
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter') onClick();
      }}
    >
      {cognitoIds.length > 0 ? (
        <AvatarWrap onClick={e => e.stopPropagation()}>
          <CustomAvatar
            cognitoIds={cognitoIds}
            handleGroupAvatar={handleGroupAvatar}
            PlaceholderImage={
              <IconCircle $color={accentColor}>
                <NotificationCategoryIcon category={category} />
              </IconCircle>
            }
          />
        </AvatarWrap>
      ) : (
        <IconCircle $color={accentColor}>
          <NotificationCategoryIcon category={category} />
        </IconCircle>
      )}
      <Body>
        <TitleRow>
          <TitleText $isUnread={isUnread}>{title}</TitleText>
          {isUnread && (
            <UnreadDot
              aria-label={t('Mark as read')}
              onClick={e => {
                e.stopPropagation();
                onToggleRead(id, 'READ');
              }}
            />
          )}
        </TitleRow>
        {isShowInline && <Preview>{firstLineContent}</Preview>}
        <Meta>
          <time dateTime={created} title={timeAgo.formatTooltip(createdDate)}>
            {timeAgo.format(createdDate)}
          </time>
          {category !== 'account' && type !== 'newTrade' && accountTradeLabel}
        </Meta>
      </Body>
      {deeplink && <Cta>{t('View')} →</Cta>}
    </Container>
  );
};

export default Notification;
